import { useTranslation } from '../i18n'
import type { ProjectBudget } from '../types'

interface BudgetSummaryProps {
  budget: ProjectBudget | null
  loading: boolean
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export function BudgetSummary({ budget, loading }: BudgetSummaryProps) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="grid grid-cols-3" style={{ marginBottom: '1.5rem' }}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="stat-card">
            <span className="stat-value">--</span>
            <span className="stat-label">{t('label.loading')}</span>
          </div>
        ))}
      </div>
    )
  }

  if (!budget) {
    return null
  }

  return (
    <div className="grid grid-cols-3" style={{ marginBottom: '1.5rem' }}>
      <div className="stat-card" data-testid="budget-total-tests">
        <span className="stat-value">{budget.totalTestCount}</span>
        <span className="stat-label">{t('label.totalTests')}</span>
      </div>
      <div className="stat-card" data-testid="budget-passing-tests">
        <span className="stat-value">{budget.passingTestCount}</span>
        <span className="stat-label">{t('label.passingTests')}</span>
      </div>
      <div className="stat-card" data-testid="budget-skipped-tests">
        <span className="stat-value">{budget.skippedTestCount}</span>
        <span className="stat-label">{t('label.skippedTests')}</span>
      </div>
      <div className="stat-card" data-testid="budget-remaining-hours">
        <span className="stat-value stat-success">
          {budget.projectedRemainingHours.toFixed(1)} h
        </span>
        <span className="stat-label">{t('label.projectedRemainingHours')}</span>
      </div>
      <div className="stat-card" data-testid="budget-projected-hours">
        <span className="stat-value">{budget.projectedTotalHours.toFixed(1)} h</span>
        <span className="stat-label">{t('label.projectedTotalHours')}</span>
      </div>
      <div className="stat-card" data-testid="budget-fte">
        <span className="stat-value">
          {budget.activeProjectCount > 1
            ? t('label.effectiveFte', {
                fte: (budget.fteCount / budget.activeProjectCount).toFixed(1),
                total: budget.fteCount,
              })
            : budget.fteCount}
        </span>
        <span className="stat-label">{t('label.fteLabel')}</span>
      </div>
      {budget.projectedEndDate && (
        <div className="stat-card" data-testid="budget-end-date">
          <span className="stat-value stat-success">{formatDate(budget.projectedEndDate)}</span>
          <span className="stat-label">{t('label.projectedEnd')}</span>
        </div>
      )}
    </div>
  )
}
